import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'
import Card from '@/components/Cards/Card'
import { newsArticles } from '@/data/news'
import { 
  CalendarDaysIcon,
  UserIcon,
  ArrowRightIcon,
  NewspaperIcon
} from '@heroicons/react/24/outline'

const NewsPage = () => {
  const categories = [
    'All Categories',
    'Company News',
    'Partnerships',
    'Financial News',
    'Events',
    'Industry Insights',
    'Corporate Social Responsibility'
  ]

  const featuredArticles = newsArticles.filter(article => article.featured)
  const regularArticles = newsArticles.filter(article => !article.featured)

  return (
    <>
      <Helmet>
        <title>News & Insights - Nexus Global Solutions</title>
        <meta name="description" content="Stay updated with the latest news, insights, and developments from Nexus Global Solutions. Read about our partnerships, market expansion, and industry expertise." />
        <meta name="keywords" content="nexus global news, business insights, international partnerships, market expansion, corporate updates" />
      </Helmet>
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary-800 to-accent-800 text-white py-20">
        <div className="corporate-container">
          <div className="max-w-4xl">
            <h1 className="font-display text-4xl md:text-6xl font-bold mb-6">
              News & Insights
            </h1>
            <p className="text-xl text-primary-100 mb-8 leading-relaxed">
              Stay informed about our latest developments, strategic partnerships, 
              market insights, and thought leadership in international business consulting.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
              <div className="scraping-target" data-news-stat="total-articles">
                <div className="text-3xl font-bold text-gold-200">{newsArticles.length}</div>
                <div className="text-primary-200">Published Articles</div>
              </div>
              <div className="scraping-target" data-news-stat="categories">
                <div className="text-3xl font-bold text-gold-200">{categories.length - 1}</div>
                <div className="text-primary-200">Content Categories</div>
              </div>
              <div className="scraping-target" data-news-stat="featured">
                <div className="text-3xl font-bold text-gold-200">{featuredArticles.length}</div>
                <div className="text-primary-200">Featured Stories</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Articles */}
      <section className="corporate-section bg-white">
        <div className="corporate-container">
          <div className="text-center mb-16">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-primary-900 mb-6">
              Featured Stories
            </h2>
            <p className="text-xl text-primary-700 max-w-3xl mx-auto">
              Highlighting our most significant announcements and strategic developments 
              in international business consulting.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {featuredArticles.map((article) => (
              <Link key={article.id} to={`/news/${article.slug}`}>
                <Card hover className="h-full scraping-target" data-article={article.id}>
                  <div className="mb-6">
                    <div className="w-full h-48 bg-gradient-to-br from-primary-800 to-accent-800 rounded-lg mb-6 flex items-center justify-center">
                      <NewspaperIcon className="h-16 w-16 text-white" />
                    </div>
                    
                    <div className="flex items-center justify-between mb-4">
                      <span className="px-3 py-1 bg-accent-100 text-accent-800 text-sm font-medium rounded-full scraping-target" data-category={article.category.toLowerCase().replace(/\s+/g, '-')}>
                        {article.category}
                      </span>
                      <span className="text-primary-500 text-sm scraping-target" data-read-time={article.readTime}>
                        {article.readTime}
                      </span>
                    </div>

                    <h3 className="font-display text-xl font-bold text-primary-900 mb-3 scraping-target" data-title={article.slug}>
                      {article.title}
                    </h3>
                    
                    <p className="text-primary-700 mb-4 leading-relaxed" data-excerpt={article.id}>
                      {article.excerpt}
                    </p>
                    
                    <div className="flex items-center justify-between text-sm text-primary-600">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-2 scraping-target" data-author={article.author.toLowerCase().replace(/\s+/g, '-')}>
                          <UserIcon className="h-4 w-4" />
                          <span>{article.author}</span>
                        </div>
                        <div className="flex items-center space-x-2 scraping-target" data-publish-date={article.publishedAt}>
                          <CalendarDaysIcon className="h-4 w-4" />
                          <span>{new Date(article.publishedAt).toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          })}</span>
                        </div>
                      </div>
                      <ArrowRightIcon className="h-4 w-4 text-accent-800" />
                    </div>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* All Articles */}
      <section className="corporate-section bg-primary-50">
        <div className="corporate-container">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl font-bold text-primary-900 mb-6">
              Latest Updates
            </h2>
            <p className="text-xl text-primary-700 max-w-3xl mx-auto">
              Comprehensive coverage of our business developments, market insights, 
              and industry expertise.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {regularArticles.map((article) => (
              <Link key={article.id} to={`/news/${article.slug}`}>
                <Card hover className="h-full scraping-target" data-article={article.id}>
                  <div className="w-full h-32 bg-gradient-to-br from-steel-700 to-primary-800 rounded-lg mb-4 flex items-center justify-center">
                    <NewspaperIcon className="h-8 w-8 text-white" />
                  </div>
                  
                  <div className="flex items-center justify-between mb-3">
                    <span className="px-2 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full scraping-target" data-category={article.category.toLowerCase().replace(/\s+/g, '-')}>
                      {article.category}
                    </span>
                    <span className="text-primary-500 text-xs scraping-target" data-read-time={article.readTime}>
                      {article.readTime}
                    </span>
                  </div>

                  <h3 className="font-display text-lg font-bold text-primary-900 mb-2 scraping-target" data-title={article.slug}>
                    {article.title}
                  </h3>
                  
                  <p className="text-primary-700 text-sm mb-4 leading-relaxed" data-excerpt={article.id}>
                    {article.excerpt}
                  </p>
                  
                  <div className="flex items-center justify-between text-xs text-primary-600">
                    <div className="flex items-center space-x-2 scraping-target" data-author={article.author.toLowerCase().replace(/\s+/g, '-')}>
                      <UserIcon className="h-3 w-3" />
                      <span>{article.author}</span>
                    </div>
                    <div className="flex items-center space-x-2 scraping-target" data-publish-date={article.publishedAt}>
                      <CalendarDaysIcon className="h-3 w-3" />
                      <span>{new Date(article.publishedAt).toLocaleDateString('en-US', { 
                        month: 'short', 
                        day: 'numeric' 
                      })}</span>
                    </div>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Signup */}
      <section className="corporate-section bg-white">
        <div className="corporate-container">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="font-display text-3xl font-bold text-primary-900 mb-6">
              Stay Updated
            </h2>
            <p className="text-xl text-primary-700 mb-8">
              Subscribe to our newsletter for the latest insights on international business 
              and market opportunities.
            </p>
            <div className="flex max-w-md mx-auto">
              <input 
                type="email" 
                placeholder="Enter your email address"
                className="form-input rounded-r-none flex-1"
              />
              <button className="btn-primary rounded-l-none px-6">
                Subscribe
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Educational data for scraping practice */}
      <div className="educational-data" style={{ display: 'none' }}>
        <div data-page="news" data-content-type="news-articles">
          <span data-total-articles={newsArticles.length}>{newsArticles.length} Total Articles</span>
          <span data-featured-articles={featuredArticles.length}>{featuredArticles.length} Featured Articles</span>
          {newsArticles.map(article => (
            <div key={article.id} data-article-data={article.id}>
              <span data-article-title={article.title}>{article.title}</span>
              <span data-article-author={article.author}>{article.author}</span>
              <span data-article-date={article.publishedAt}>{article.publishedAt}</span>
              <span data-article-category={article.category}>{article.category}</span>
              <span data-article-slug={article.slug}>{article.slug}</span>
            </div>
          ))}
        </div>
      </div>
    </>
  )
}

export default NewsPage
